<script lang="ts" setup>
    import defaultImage from '@/assets/images/no-image-fachkreis.jpg';
    import { sanitizeHtml } from '~/utils/sanitizeHtml';

    const props = defineProps<{
        searchType?: string;
        imageId?: number;
        title?: string;
        text?: string;
        linkTo?: string;
        productPrescriptionOnly?: boolean;
        productIndications?: string | string[];
        productApplications?: string | string[];
        documentType?: string;
        documentTotalChunks?: number;
        documentChunks?: string[];
        highlight?: string[];
        date?: string;
    }>();

    const { t, d } = useI18n();

    const route = useRoute();
    const { q } = route.query;

    const labelType = ref({ text: '', button: '', class: '' });
    const indications = computed(() =>
        Array.isArray(props.productIndications)
            ? props.productIndications
            : props.productIndications
              ? [props.productIndications]
              : []
    );

    const applications = computed(() =>
        Array.isArray(props.productApplications)
            ? props.productApplications
            : props.productApplications
              ? [props.productApplications]
              : []
    );

    const joinedApplicationsText = computed(() => {
        const text = applications.value.join(', ');
        return text.length > 150 ? `${text.substring(0, 150).trimEnd()} …` : text;
    });

    const joinedIndicationsText = computed(() => {
        const text = indications.value.join(', ');
        return text.length > 150 ? `${text.substring(0, 150).trimEnd()} …` : text;
    });

    const highlight = computed(() => [...new Set(props.highlight)]);

    switch (props.searchType) {
        case 'product':
            labelType.value = {
                text: t('fachkreis.searchCard.label.product'),
                button: t('fachkreis.searchCard.buttonText.product'),
                class: '--product'
            };
            break;
        case 'document':
            labelType.value = {
                text: t('fachkreis.searchCard.label.document'),
                button: t('fachkreis.searchCard.buttonText.document'),
                class: '--document'
            };
            break;
        case 'news':
            labelType.value = {
                text: t('fachkreis.searchCard.label.news'),
                button: t('fachkreis.searchCard.buttonText.news'),
                class: '--news'
            };
            break;
        case 'fachkreis':
            labelType.value = {
                text: t('fachkreis.searchCard.label.area'),
                button: t('fachkreis.searchCard.buttonText.area'),
                class: '--area'
            };
            break;
        default:
            console.log('invalid search type');
    }

    const onImageError = (event: Event) => {
        (event.target as HTMLImageElement).src = defaultImage;
    };

    function trimText(text: string) {
        const maxLength = 54; // totally random
        const halfLength = Math.floor(maxLength / 2);
        const relevantChunk = text.toLowerCase().indexOf(q.toString().toLowerCase());

        if (relevantChunk === -1) return `${text.substring(0, maxLength)}...`;

        let start = Math.max(relevantChunk - halfLength, 0);
        let end = Math.min(relevantChunk + halfLength, text.length);

        // prevents cut words
        while (start > 0 && text[start] !== ' ') {
            start--;
        }

        while (end < text.length && text[end] !== ' ') {
            end++;
        }

        return `${start > 0 ? '... ' : ''}${text.substring(start, end)}${end < text.length ? ' ...' : ''}`;
    }
</script>

<template>
    <NuxtLink
        v-bind="searchType === 'document' ? { download: '' } : {}"
        class="search-result-card"
        data-testid="fachportal-search-result-card"
        :data-search-type="searchType"
        :to="linkTo"
    >
        <div class="content">
            <div>
                <span :class="labelType.class">
                    {{ labelType.text }}
                </span>

                <h2 :class="{ '--date-card': searchType === 'news' && date }">{{ title }}</h2>

                <div
                    v-if="searchType === 'product'"
                    class="product-info"
                >
                    <p
                        v-if="productPrescriptionOnly"
                        class="prescription-text"
                    >
                        {{ t('fachkreis.searchCard.product.prescriptionOnly') }}
                    </p>

                    <p
                        v-if="indications.length"
                        class="indication-title"
                    >
                        {{ t('fachkreis.searchCard.indications', indications.length) }}:
                    </p>
                    <p
                        v-if="indications.length"
                        class="indications-text"
                    >
                        {{ joinedIndicationsText }}
                    </p>

                    <p
                        v-if="applications.length"
                        class="indication-title"
                    >
                        {{ t('fachkreis.searchCard.applications', applications.length) }}:
                    </p>
                    <p
                        v-if="applications.length"
                        class="indications-text"
                    >
                        {{ joinedApplicationsText }}
                    </p>
                </div>

                <div
                    v-if="searchType === 'document'"
                    class="document-info"
                >
                    <p
                        v-if="documentType"
                        class="document-type"
                    >
                        {{ documentType }}
                    </p>

                    <div>
                        <p
                            v-if="documentTotalChunks"
                            class="search-hits"
                        >
                            {{ t('search.documentHits') }} {{ documentTotalChunks }}
                        </p>

                        <ol
                            v-if="
                                documentTotalChunks !== undefined &&
                                documentTotalChunks > 0 &&
                                documentChunks &&
                                documentChunks.length >= 1
                            "
                            class="chunk-container"
                        >
                            <li
                                v-for="chunk in documentChunks"
                                :key="chunk"
                                class="chunk"
                            >
                                {{ trimText(chunk) }}
                            </li>
                        </ol>
                    </div>
                </div>

                <div
                    v-if="searchType === 'fachkreis' || searchType === 'news'"
                    class="document-info"
                >
                    <p
                        v-if="documentType"
                        class="document-type"
                    >
                        {{ documentType }}
                    </p>

                    <p
                        v-if="searchType === 'news' && date"
                        class="date"
                    >
                        {{ d(date) }}
                    </p>

                    <div>
                        <p
                            v-if="highlight.length"
                            class="search-hits"
                        >
                            {{ t('search.documentHits') }} {{ highlight.length }}
                        </p>

                        <ol
                            v-if="highlight.length >= 1"
                            class="chunk-container"
                        >
                            <!-- eslint-disable vue/no-v-html -->
                            <li
                                v-for="chunk in highlight"
                                :key="chunk"
                                class="chunk"
                                v-html="sanitizeHtml(trimText(chunk))"
                            />
                            <!-- eslint-enable vue/no-v-html -->
                        </ol>
                    </div>
                </div>

                <p v-if="text">
                    {{ text }}
                </p>
            </div>

            <PrimaryButton
                :button-text="labelType.button"
                :product-button="true"
            />
        </div>

        <div class="media">
            <CenshareImage
                v-if="imageId && searchType === 'product'"
                :image-id="imageId"
                type="productCard"
                :aria-hidden="true"
            />

            <img
                v-else-if="searchType === 'document'"
                src="~/assets/icons/file-down.svg"
                class="--svg"
                alt="no-image"
                aria-hidden="true"
                @error="onImageError"
            />

            <img
                v-else-if="searchType === 'fachkreis'"
                src="~/assets/icons/file-text.svg"
                class="--svg"
                alt="no-image"
                aria-hidden="true"
                @error="onImageError"
            />

            <img
                v-else-if="searchType === 'news'"
                src="~/assets/icons/newspaper.svg"
                class="--svg"
                alt="no-image"
                aria-hidden="true"
                @error="onImageError"
            />

            <img
                v-else
                :src="defaultImage"
                class="--cover"
                alt="no-image"
                aria-hidden="true"
                @error="onImageError"
            />
        </div>
    </NuxtLink>
</template>

<style scoped lang="scss">
    @import '@/assets/scss/fachkreis/fachkreis-search-card.scss';
</style>
